
import { useCallback, useEffect, useState } from "react";
import {
    Card,
    Col,
    Collapse,
    DatePicker,
    Empty,
    Input,
    List,
    message,
    Pagination,
    Row,
    Skeleton,
    Space,
    Statistic,
    Tag,
    Typography,
} from "antd";
import {
    ArrowDownOutlined,
    ArrowUpOutlined,
    BankOutlined,
    CreditCardOutlined,
} from "@ant-design/icons";
import { requestError } from "../controllers/functions";
import dayjs from "dayjs";
import axios from "axios";

const { Text, Title } = Typography;

const StudentAccountStatement = () => {
    const [accountStatements, setAccountStatements] = useState({});
    const [loading, setLoading] = useState(false);
    const [pageSize, setPageSize] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchString, setSearchString] = useState("");

    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);

    const getTerm = useCallback(async () => {
        const headers = { authorization: `Bearer ${localStorage.getItem("token")}` };
        try {
          const [term ] = await Promise.all([
            axios.get(process.env.REACT_APP_API_URI + `/school/terms/current`, { headers }),
          ]);
          if( term?.data?.startDate && term?.data?.endDate ) {
            setStartDate(term?.data?.startDate);
            setEndDate(term?.data?.endDate);
          } else {
            message.warning("No current school term found. Please set a current school term to view student account statements.");
          }
        } catch (error) {
          requestError({ error });
        }
      }, []);
    
      useEffect( () => {
        getTerm();
      }, [getTerm]);

    const fetchAccountStatements = useCallback(async () => {
        setLoading(true);

        try {
            const q = new URLSearchParams({
                pageSize,
                currentPage,

                ...(searchString?.trim() && {
                    searchString: searchString.trim(),
                }),

                ...(startDate && {
                    startDate: startDate,
                }),

                ...(endDate && {
                    endDate: endDate,
                }),
            }).toString();

            const response = await fetch(
                `${process.env.REACT_APP_API_URI}/student/payments/statements?${q}`,
                {
                    headers: {
                        authorization: `Bearer ${localStorage.getItem("token")}`,
                    },
                }
            );

            if (!response.ok) {
                throw new Error("Failed to fetch account statements");
            }

            const data = await response.json();
            setAccountStatements(data);
        } catch (error) {
            console.error(error);
            requestError({ error });
        } finally {
            setLoading(false);
        }
    }, [
        pageSize,
        currentPage,
        searchString,
        startDate,
        endDate,
    ]);

    useEffect(() => {
        if (!startDate || !endDate) return;
        const timeout = setTimeout(() => {
            fetchAccountStatements();
        }, 300);

        return () => clearTimeout(timeout);
    }, [endDate, fetchAccountStatements, startDate]);

    const formatAmount = (amount, currency = "USD") => {
        return `${currency} ${Number(amount || 0).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        })}`;
    };

    const getPaymentAmount = payment => {
        const amount = Number(payment?.amount || 0);
        const rate = Number(payment?.exchangeRate?.rate || 0);

        return rate > 0 ? amount / rate : amount;
    };

    const getAncillaryAmount = record => {
        const amount = Number(record?.ancillaryFee?.amount || record?.amount || 0) - Number(record?.totalDiscounts || 0);
        const rate = Number(record?.exchangeRate?.rate || 0);

        return rate > 0 ? amount / rate : amount;
    };

    const students = accountStatements?.studentAccounts || [];

    const renderStudentStatement = student => {
        const currency = student?.currency?.name || "USD";
        const fees = student?.fees || [];
        const ancillaryFees = student?.ancillaryFees || [];
        const payments = student?.payments || [];


        const totalDiscounts = record => record?.discounts?.reduce(
            (totalDiscount, discountRecord) => {

                const discount = discountRecord?.discount;
                if (!discount) return totalDiscount;

                const discountValue = discount.isPercentage
                    ? (getAncillaryAmount(record) * discount.percentage) / 100
                    : Number(discount.value || 0);
                return totalDiscount + discountValue;
            },
            0
        );

        const totalSchoolFees = fees.reduce(
            (total, fee) => total + Number(fee?.amount - totalDiscounts(fee) || 0),
            0
        );

        const totalAncillaryFees = ancillaryFees.reduce(
            (total, record) =>{
                return  total + getAncillaryAmount({...record, totalDiscounts : totalDiscounts(record) });
            },
            0
        );

        const totalCharges = totalSchoolFees + totalAncillaryFees;

        const totalPayments = payments.reduce(
            (total, payment) => total + getPaymentAmount(payment),
            0
        );

        const closingBalance = Number(student?.closingBalance || 0);

        const getTransactionDescription = (record) => {
            if (record?.discounts?.length) {
                return record.discounts
                    .map(({ discount }) => {
                        if (!discount) return null;

                        const discountValue = discount.isPercentage
                            ? `${discount.percentage}%`
                            : `${discount.currency?.name || ""} ${discount.value}`;

                        return `${discount.name} discount (${discountValue})`;
                    })
                    .filter(Boolean)
                    .join(", ");
            }

            return record?.description || "";
        };
        return {
            key: student.studentId,

            label: (
                <Row justify="space-between" align="middle" gutter={[12, 8]}>
                    <Col>
                        <Space direction="vertical" size={0}>
                            <Text strong>{student.studentName}</Text>

                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {student.registrationNumber}
                            </Text>
                        </Space>
                    </Col>

                    <Col>
                        <Space>
                            <Tag
                                color={
                                    closingBalance < 0
                                        ? "red"
                                        : closingBalance > 0
                                            ? "green"
                                            : "default"
                                }
                            >
                                {closingBalance < 0
                                    ? "Owing"
                                    : closingBalance > 0
                                        ? "Credit"
                                        : "Cleared"}
                            </Tag>

                            <Text strong>
                                {formatAmount(closingBalance, currency)}
                            </Text>
                        </Space>
                    </Col>
                </Row>
            ),

            children: (
                <Space
                    direction="vertical"
                    size="middle"
                    style={{ width: "100%" }}
                >
                    <Row gutter={[12, 12]}>
                        <Col xs={24} sm={12} lg={6}>
                            <Card size="small">
                                <Statistic
                                    title="Opening Balance"
                                    value={Number(student.openingBalance || 0)}
                                    precision={2}
                                    prefix={currency}
                                />
                            </Card>
                        </Col>

                        <Col xs={24} sm={12} lg={6}>
                            <Card size="small">
                                <Statistic
                                    title="Total Charges"
                                    value={totalCharges}
                                    precision={2}
                                    prefix={currency}
                                    valueStyle={{ color: "#cf1322" }}
                                    suffix={<ArrowUpOutlined />}
                                />
                            </Card>
                        </Col>

                        <Col xs={24} sm={12} lg={6}>
                            <Card size="small">
                                <Statistic
                                    title="Total Payments"
                                    value={totalPayments}
                                    precision={2}
                                    prefix={currency}
                                    valueStyle={{ color: "#389e0d" }}
                                    suffix={<ArrowDownOutlined />}
                                />
                            </Card>
                        </Col>

                        <Col xs={24} sm={12} lg={6}>
                            <Card size="small">
                                <Statistic
                                    title="Closing Balance"
                                    value={closingBalance}
                                    precision={2}
                                    prefix={currency}
                                    valueStyle={{
                                        color:
                                            closingBalance < 0
                                                ? "#cf1322"
                                                : "#389e0d",
                                    }}
                                />
                            </Card>
                        </Col>
                    </Row>

                    <Row gutter={[12, 12]}>
                        <Col xs={24} lg={12}>
                            <Card
                                size="small"
                                title={
                                    <Space>
                                        <BankOutlined />
                                        Charges
                                    </Space>
                                }
                                extra={
                                    <Text strong type="danger">
                                        {formatAmount(totalCharges, currency)}
                                    </Text>
                                }
                            >
                                <List
                                    size="small"
                                    dataSource={[
                                        ...fees.map(fee => ({
                                            key: `fee-${fee.id}`,
                                            title:
                                                fee?.schoolTerm?.name ||
                                                "School Fee",
                                            date:
                                                fee?.schoolTerm?.startDate ||
                                                fee?.date,
                                            amount:`${fee?.currency} ${fee?.amount}`,
                                            description : `${fee?.schoolTerm?.name} School fees. ${getTransactionDescription(fee)}`,
                                        })),

                                        ...ancillaryFees.map(record => ({
                                            key: `ancillary-${record.id}`,
                                            title:
                                                record?.ancillaryFee
                                                    ?.name,
                                            date:
                                                record?.ancillaryFee?.date,
                                            description: `Billed on ${dayjs(record?.ancillaryFee?.date).format("ddd, DD MMM YYYY")}. ${record?.ancillaryFee?.description || ""}. ${getTransactionDescription(record)}. ${record?.exchangeRate ? `Exchange rate: 1: ${record?.currency} ${record?.exchangeRate?.rate} has been applied.` : ''} `,
                                            amount:`${record?.currency} ${record?.ancillaryFee?.amount}`,
                                        })),
                                    ]}
                                    locale={{ emptyText: "No charges found" }}
                                    renderItem={item => (
                                        <List.Item
                                            key={item.key}
                                            extra={
                                                <Text strong type="danger">
                                                    {item.amount}
                                                </Text>
                                            }
                                        >
                                            <List.Item.Meta
                                                title={item.title}
                                                description={
                                                    <>
                                                        {item.description}
                                                    </>
                                                }
                                            />
                                        </List.Item>
                                    )}
                                    footer={
                                        <Row gutter={[12, 12]}  style={{ paddingRight:"1rem", paddingLeft:"1rem"}} justify="space-between">
                                            <Text strong>Total charges</Text>

                                            <Text strong type="danger">
                                                {formatAmount(
                                                    totalCharges,
                                                    currency
                                                )}
                                            </Text>
                                        </Row>
                                    }
                                />
                            </Card>
                        </Col>

                        <Col xs={24} lg={12}>
                            <Card
                                size="small"
                                title={
                                    <Space>
                                        <CreditCardOutlined />
                                        Payments
                                    </Space>
                                }
                                extra={
                                    <Text strong type="success">
                                        {formatAmount(totalPayments, currency)}
                                    </Text>
                                }
                            >
                                <List
                                    size="small"
                                    dataSource={payments}
                                    locale={{ emptyText: "No payments found" }}
                                    renderItem={payment => (
                                        <List.Item
                                            key={payment.id}
                                            extra={
                                                <Text strong type="success">
                                                    {`${payment.currency} ${payment.amount}`}
                                                </Text>
                                            }
                                        >
                                            <List.Item.Meta
                                                title={
                                                    `${payment.paymentNotes || "Student payment"} [Receipt No. ${payment.id}]`
                                                }
                                                description={
                                                    <>
                                                        {dayjs(payment.date).format("ddd, DD MMM YYYY")}. Method: {payment.paymentMethod?.toUpperCase()}. {payment?.exchangeRate ? `Exchange rate: 1: ${payment?.currency} ${payment.exchangeRate?.rate} has been applied` : ``}
                                                    </>
                                                }
                                            />
                                        </List.Item>
                                    )}
                                    footer={
                                        <Row gutter={[12, 12]} style={{ paddingRight:"1rem", paddingLeft:"1rem"}} justify="space-between">
                                            <Text strong>Total payments</Text>

                                            <Text strong type="success">
                                                {formatAmount(
                                                    totalPayments,
                                                    currency
                                                )}
                                            </Text>
                                        </Row>
                                    }
                                />
                            </Card>
                        </Col>
                    </Row>
                </Space>
            ),
        };
    };

    return (
        <div>
            <Row
                justify="space-between"
                align="bottom"
                gutter={[12, 12]}
                style={{ marginBottom: 16 }}
            >
                <Col xs={24} lg={8}>
                    <Title level={3} style={{ margin: 0 }}>
                        Student Account Statements
                    </Title>

                    <Text type="secondary">
                        View charges, payments and account balances
                    </Text>
                </Col>

                <Col xs={24} sm={8} lg={5}>
                    <Text type="secondary">Start date</Text>

                    <DatePicker
                        value={startDate? dayjs(startDate) : null}
                        format="DD/MM/YYYY"
                        placeholder="Select start date"
                        style={{ width: "100%", marginTop: 4 }}
                        onChange={date => {
                            setStartDate(date.format("YYYY-MM-DD"));
                            setCurrentPage(1);
                        }}
                        disabledDate={date =>
                            endDate && date?.isAfter(endDate, "day")
                        }
                    />
                </Col>

                <Col xs={24} sm={8} lg={5}>
                    <Text type="secondary">End date</Text>

                    <DatePicker
                        value={endDate? dayjs(endDate) : null}
                        format="DD/MM/YYYY"
                        placeholder="Select end date"
                        style={{ width: "100%", marginTop: 4 }}
                        onChange={date => {
                            setEndDate(date.format("YYYY-MM-DD"));
                            setCurrentPage(1);
                        }}
                        disabledDate={date =>
                            startDate && date?.isBefore(startDate, "day")
                        }
                    />
                </Col>

                <Col xs={24} sm={8} lg={6}>
                    <Text type="secondary">Search student</Text>

                    <Input.Search
                        allowClear
                        placeholder="Name or registration number"
                        value={searchString}
                        style={{ marginTop: 4 }}
                        onChange={event => {
                            setSearchString(event.target.value);
                            setCurrentPage(1);
                        }}
                    />
                </Col>
            </Row>

            {loading ? (
                <Card>
                    <Skeleton active />
                </Card>
            ) : students.length === 0 ? (
                <Card>
                    <Empty description="No account statements found" />
                </Card>
            ) : (
                <>
                    <Collapse
                        accordion
                        items={students.map(renderStudentStatement)}
                    />

                    <Row justify="end" style={{ marginTop: 16 }}>
                        <Pagination
                            current={currentPage}
                            pageSize={pageSize}
                            total={
                                accountStatements?.totalStudents
                            }
                            showSizeChanger
                            showTotal={total => `${total} students`}
                            onChange={(page, size) => {
                                setCurrentPage(page);
                                setPageSize(size);
                            }}
                        />
                    </Row>
                </>
            )}
        </div>
    );
};

export default StudentAccountStatement;